import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function UpsellSheet({ isOpen, onClose, suggestions = [], cart, onUpdateQuantity }) {
    const timerRef = useRef(null);

    // Auto-dismiss after 5 seconds
    useEffect(() => {
        if (isOpen) {
            timerRef.current = setTimeout(onClose, 5000);
        }
        return () => clearTimeout(timerRef.current);
    }, [isOpen, onClose]);

    const cancelTimer = () => clearTimeout(timerRef.current);

    const items = suggestions.slice(0, 3);
    if (!items.length) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '100%', opacity: 0 }}
                    transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                    onPointerDown={cancelTimer}
                    className="fixed bottom-24 left-0 right-0 z-[150] max-w-lg mx-auto px-3"
                >
                    <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-slate-50">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">People also add</p>
                                <h3 className="text-sm font-black text-charcoal">Goes well with your order 🍽️</h3>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors text-xs"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Suggestions */}
                        <div className="flex gap-3 px-4 py-3 overflow-x-auto no-scrollbar">
                            {items.map(dish => {
                                const qty = cart[dish.id] || 0;
                                const isVeg = dish.type === 'veg';
                                return (
                                    <div
                                        key={dish.id}
                                        className="flex-none w-[150px] bg-[#FCFAF5] rounded-2xl border border-slate-100 overflow-hidden"
                                    >
                                        {/* Image */}
                                        {dish.image_url ? (
                                            <div className="w-full h-20 overflow-hidden">
                                                <img
                                                    src={dish.image_url}
                                                    alt={dish.name}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-full h-20 bg-slate-100 flex items-center justify-center text-2xl">
                                                🍽️
                                            </div>
                                        )}

                                        <div className="p-2.5">
                                            {/* Veg badge + name */}
                                            <div className="flex items-center gap-1 mb-1">
                                                <div className={`w-2.5 h-2.5 rounded-sm border flex items-center justify-center flex-shrink-0
                                                    ${isVeg ? 'border-green-600' : 'border-red-500'}`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${isVeg ? 'bg-green-600' : 'bg-red-500'}`} />
                                                </div>
                                                <p className="text-xs font-bold text-charcoal line-clamp-1">{dish.name}</p>
                                            </div>

                                            {/* CTA */}
                                            {qty === 0 ? (
                                                <button
                                                    onClick={(e) => onUpdateQuantity(dish.id, 1, e, dish)}
                                                    className="w-full py-1.5 rounded-xl bg-primary text-charcoal text-[11px] font-black hover:bg-[#F0C900] active:scale-95 transition-colors"
                                                >
                                                    Add ₹{Number(dish.price).toFixed(0)}
                                                </button>
                                            ) : (
                                                <div className="flex items-center justify-center gap-1 bg-[#F4F2E6] rounded-xl px-1 py-1">
                                                    <button
                                                        onClick={(e) => onUpdateQuantity(dish.id, qty - 1, e, dish)}
                                                        className="w-6 h-6 flex items-center justify-center rounded-lg bg-white text-charcoal text-xs font-black"
                                                    >−</button>
                                                    <span className="w-5 text-center text-xs font-black text-charcoal">{qty}</span>
                                                    <button
                                                        onClick={(e) => onUpdateQuantity(dish.id, qty + 1, e, dish)}
                                                        className="w-6 h-6 flex items-center justify-center rounded-lg bg-primary text-charcoal text-xs font-black"
                                                    >+</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Auto-dismiss progress */}
                        <motion.div
                            initial={{ width: '100%' }}
                            animate={{ width: '0%' }}
                            transition={{ duration: 5, ease: 'linear' }}
                            className="h-0.5 bg-primary/60 mx-4 mb-3 rounded-full"
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
